// When a message was sent, in the reader's own zone — for the lab, its `userTz` setting (the Settings
// timezone), the same value it hands the engine. The product's page reads the token's
// `selectedTimezone`; the rule and the words are the same in both, the date library is each client's own.
//
// ★ THE FORMATS ARE SQ-APP'S OWN, not this page's: a time of day is `HH:mm` and a date is `MMM DD, YYYY` (with a time,
//   `MMM DD, YYYY HH:mm`) — the forms the web app shows on its screens (counted 8 Oct 2026 over its src: `MMM DD, YYYY` at 135 sites, `MMM DD, YYYY HH:mm` at 37; K-RSM10-2) and the engine
//   writes in its prose ("Jan 01, 2026 – Oct 08, 2026"). No "Yesterday", no weekday, no "12 Sep": the page reads like the
//   screens around it.
//
// ★ TODAY IS A CALENDAR DAY IN THAT ZONE, never a rolling 24 hours:
//
//     today            14:32
//     any other day    Sep 12, 2026 14:32
//
// An absent zone is UTC (the page's "Not set (UTC)", which the engine counts in too); a name the browser cannot read falls back to
// its local clock. Pure: the caller passes `now`.
//
// ★ THE DAY ARITHMETIC IS DONE ON WALL-CLOCK DATES, NOT WITH dayjs. The timezone plugin FORMATS a zone's
//   clock correctly, but its startOf / diff / add drift by an hour across a DST change. Measured against
//   the product's moment-timezone version over 20,000 seeded instants in five zones: 35 disagreements,
//   every one at a clocks-change — a message seven calendar days old called "Wed" across the spring
//   change, and Sydney's next midnight an hour out. So each moment is reduced to the date its zone's
//   clock shows, counted as a whole UTC day, and days are subtracted as integers.
//
// `dayjs/plugin/…js`, WITH the extension, for the same reason resultDates.mjs gives: the extensionless
// form builds under CRA and is unloadable under node's ESM resolver.
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc.js";
import timezonePlugin from "dayjs/plugin/timezone.js";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const DAY_MS = 24 * 60 * 60 * 1000;

const toMs = (instant) => {
  if (instant instanceof Date) return instant.getTime();
  if (typeof instant === "number") return instant;
  if (typeof instant === "string" && instant) return Date.parse(instant);
  return NaN;
};

const knownZone = (zone) => {
  if (!zone || typeof zone !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return true;
  } catch (e) {
    return false;
  }
};

// The zone's wall clock for an instant (dayjs's formatting half, which is reliable). Exported for the result
// table (resultDates), which only formats — so a listing's date-and-time reads on the same clock as the message
// times and the engine's prose.
export const inZone = (ms, zone) => (knownZone(zone) ? dayjs(ms).tz(zone) : dayjs(ms));
// The date that clock shows, as a whole day count.
const dayNumber = (wall) => Date.UTC(wall.year(), wall.month(), wall.date()) / DAY_MS;

// A page with no zone set ("Not set (UTC)") reads in UTC — the zone the engine counts in for it — never the browser's clock.
const readerZone = (zone) => zone || "UTC";

export function formatMessageTime(instant, { zone: given, now = Date.now() } = {}) {
  const zone = readerZone(given);
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return null;
  const at = inZone(ms, zone);
  const clock = at.format("HH:mm");
  if (dayNumber(inZone(now, zone)) === dayNumber(at)) return clock;
  return `${at.format("MMM DD, YYYY")} ${clock}`;
}

// A chat list row's time: coarser than a message's — the clock on today's rows, the date on every other day's — in the same
// zone and the same forms, so the list and the thread under it never disagree about a day.
export function formatRailTime(instant, { zone: given, now = Date.now() } = {}) {
  const zone = readerZone(given);
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return "";
  const at = inZone(ms, zone);
  return dayNumber(inZone(now, zone)) === dayNumber(at) ? at.format("HH:mm") : at.format("MMM DD, YYYY");
}

// The whole moment, for the tooltip, with the zone named so the reader can see which clock it is on.
export function fullMessageTime(instant, { zone: given } = {}) {
  const zone = readerZone(given);
  const ms = toMs(instant);
  if (!Number.isFinite(ms)) return "";
  const text = inZone(ms, zone).format("MMM DD, YYYY HH:mm");
  return knownZone(zone) ? `${text} (${zone})` : text;
}

// How long until the reader's next midnight. The instant is found from the wall clock: tomorrow's date
// at 00:00 read as UTC, corrected by the zone's offset — measured twice, so a DST change that day counts.
export function msUntilNextDay(now, given) {
  const zone = readerZone(given);
  const n = inZone(now, zone);
  const offsetAt = (ms) => {
    const w = inZone(ms, zone);
    return Date.UTC(w.year(), w.month(), w.date(), w.hour(), w.minute(), w.second()) - Math.floor(ms / 1000) * 1000;
  };
  const tomorrow = Date.UTC(n.year(), n.month(), n.date() + 1);
  let midnight = tomorrow - offsetAt(tomorrow);
  midnight = tomorrow - offsetAt(midnight);
  return Math.max(1000, midnight - now);
}

// A question is stamped when SENT, an answer when it FINISHES; a stamp never moves, and the same object
// comes back when nothing new was stamped.
export function stampTimes(times, messages, now = Date.now()) {
  const base = times || {};
  let out = base;
  for (const m of Array.isArray(messages) ? messages : []) {
    if (!m || !m.id || out[m.id] != null) continue;
    if (m.role !== "user" && m.done !== true) continue;
    if (out === base) out = { ...base };
    out[m.id] = now;
  }
  return out;
}
